import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, FolderKanban, Image as ImageIcon, FileText, Video, ArrowUpRight, Mail, Calendar, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../config';

const StatCard = ({ title, value, icon: Icon, delay, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
    onClick={onClick}
    className="bg-neutral-900/50 backdrop-blur-md border border-neutral-800 p-6 rounded-2xl flex flex-col gap-4 cursor-pointer hover:border-neutral-700 hover:bg-neutral-900/80 transition-all group"
  >
    <div className="flex items-center justify-between">
      <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-500">
        <Icon size={24} />
      </div>
      <ArrowUpRight size={20} className="text-neutral-500 group-hover:text-amber-500 transition-colors" />
    </div>
    <div>
      <h3 className="text-neutral-400 text-sm font-medium">{title}</h3>
      <p className="text-3xl font-bold text-white mt-1">{value}</p>
    </div>
  </motion.div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    leads: 0,
    projects: 0,
    gallery: 0,
    videos: 0,
    pages: 0,
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [leadsRes, projectsRes, galleryRes, videosRes, pagesRes] = await Promise.all([
          axios.get(`${API_URL}/leads?limit=1000`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/projects`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/gallery`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/videos`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/pages`).catch(() => ({ data: [] })),
        ]);

        setStats({
          leads: leadsRes.data.length || 0,
          projects: projectsRes.data.length || 0,
          gallery: galleryRes.data.length || 0,
          videos: videosRes.data.length || 0,
          pages: pagesRes.data.length || 0,
        });

        // Get last 5 leads
        setRecentLeads((leadsRes.data || []).slice(0, 5));
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-2">
        <h2 className="text-3xl font-bold tracking-tight text-white">Welcome back, Admin</h2>
        <p className="text-neutral-400">Here's an overview of your platform today.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard title="Total Leads" value={stats.leads} icon={Users} delay={0.1} onClick={() => navigate('/admin/leads')} />
            <StatCard title="Projects" value={stats.projects} icon={FolderKanban} delay={0.15} onClick={() => navigate('/admin/projects')} />
            <StatCard title="Gallery" value={stats.gallery} icon={ImageIcon} delay={0.2} onClick={() => navigate('/admin/gallery')} />
            <StatCard title="Videos" value={stats.videos} icon={Video} delay={0.25} onClick={() => navigate('/admin/videos')} />
            <StatCard title="Page Entries" value={stats.pages} icon={FileText} delay={0.3} onClick={() => navigate('/admin/pages')} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
            {/* Recent Leads */}
            <div className="lg:col-span-2 bg-neutral-900/50 backdrop-blur-md border border-neutral-800 rounded-2xl overflow-hidden">
              <div className="p-6 border-b border-neutral-800 flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Recent Leads</h3>
                <button 
                  onClick={() => navigate('/admin/leads')}
                  className="text-sm text-amber-500 hover:text-amber-400 flex items-center gap-1 transition-colors"
                >
                  View All <ExternalLink size={14} />
                </button>
              </div>
              {recentLeads.length === 0 ? (
                <div className="p-8 text-center text-neutral-500">No leads yet. They'll appear here when visitors submit the contact form.</div>
              ) : (
                <div className="divide-y divide-neutral-800/50">
                  {recentLeads.map((lead, idx) => (
                    <motion.div
                      key={lead.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + idx * 0.05 }}
                      className="p-4 px-6 flex items-center justify-between hover:bg-neutral-800/20 transition-colors"
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-500 text-sm font-bold shrink-0">
                          {lead.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-medium text-sm truncate">{lead.name}</p>
                          <p className="text-neutral-500 text-xs flex items-center gap-1">
                            <Mail size={10} /> {lead.email}
                          </p>
                        </div>
                      </div>
                      <div className="text-xs text-neutral-600 flex items-center gap-1 shrink-0 ml-4">
                        <Calendar size={10} />
                        {new Date(lead.created_at).toLocaleDateString()}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-neutral-900/50 backdrop-blur-md border border-neutral-800 p-6 rounded-2xl">
              <h3 className="text-lg font-medium text-white mb-6">Quick Actions</h3>
              <div className="space-y-3">
                <QuickAction label="Add New Project" onClick={() => navigate('/admin/projects')} />
                <QuickAction label="Upload to Gallery" onClick={() => navigate('/admin/gallery')} />
                <QuickAction label="Add Video Embed" onClick={() => navigate('/admin/videos')} />
                <QuickAction label="Edit Page Content" onClick={() => navigate('/admin/pages')} />
                <QuickAction label="Site Settings" onClick={() => navigate('/admin/settings')} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function QuickAction({ label, onClick }) {
  return (
    <button 
      onClick={onClick}
      className="w-full text-left px-4 py-3 bg-neutral-800/50 hover:bg-neutral-700/50 rounded-xl transition text-sm text-neutral-300 hover:text-white flex items-center justify-between group"
    >
      {label}
      <ArrowUpRight size={14} className="text-neutral-600 group-hover:text-amber-500 transition-colors" />
    </button>
  );
}
